import { useEffect, useMemo, useState } from "react";
import { Namespace, useT } from "@racoon/i18n";
import { Button, Sheet, SheetContent, useMediaQuery } from "@racoon/ui";
import { X, ChevronLeft, ChevronRight, Check } from "lucide-react";

export interface FilterTabItem {
  key: string;
  label: string;
  count?: number;
}

export interface ExtraFilterOption {
  value: string;
  label: string;
}

export interface ExtraFilterItem {
  key: string;
  label: string;
  options: ExtraFilterOption[];
  selectedOptions: string[];
  /** Picking an option replaces the current one instead of adding to it. */
  singleSelect?: boolean;
}

interface FilterFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (payload: { selectedTabKey: string; newSelected: ExtraFilterItem[] }) => void;
  /** When false, waste-type tabs are hidden and `selectedTabKey` in `onApply` is always `"all"`. */
  showFilterTabs?: boolean;
  filterTabs?: FilterTabItem[];
  selectedTabKey?: string;
  extraFilters?: ExtraFilterItem[];
  onExtraFilterClick?: (filter: ExtraFilterItem) => void;
}

const DEFAULT_FILTER_TABS: FilterTabItem[] = [
  { key: "all", label: "Все", count: 3 },
  { key: "so", label: "СО", count: 2 },
  { key: "tbo", label: "ТБО", count: 3 },
] as const;

const DEFAULT_FILTER_ROWS: ExtraFilterItem[] = [
  { key: "district", label: "По районам", options: [], selectedOptions: [] },
  { key: "mvo", label: "По МВО", options: [], selectedOptions: [] },
  { key: "special-equipment", label: "По типам спецтехники", options: [], selectedOptions: [] },
  { key: "waste-generator", label: "По мусорообразователям", options: [], selectedOptions: [] },
] as const;

export const FilterForm = ({
  open,
  onOpenChange,
  onApply,
  showFilterTabs = true,
  filterTabs,
  selectedTabKey,
  extraFilters,
  onExtraFilterClick,
}: FilterFormProps) => {
  const t = useT();
  const isMobileOrTablet = useMediaQuery("(max-width: 1024px)");
  const tabs =
    showFilterTabs && filterTabs && filterTabs.length > 0
      ? filterTabs
      : showFilterTabs
        ? DEFAULT_FILTER_TABS
        : [];
  const rows = extraFilters && extraFilters.length > 0 ? extraFilters : DEFAULT_FILTER_ROWS;
  const [draftTabKey, setDraftTabKey] = useState<string>("all");
  const [activeFilterLabel, setActiveFilterLabel] = useState<string | null>(null);
  const [optionSelections, setOptionSelections] = useState<Record<string, string[]>>({});
  const activeFilter = useMemo(
    () => rows.find((row) => row.key === activeFilterLabel) ?? null,
    [rows, activeFilterLabel],
  );

  const getClearedSelections = () =>
    rows.reduce<Record<string, string[]>>((acc, row) => {
      acc[row.label] = [];
      return acc;
    }, {});

  useEffect(() => {
    if (open) {
      const next: Record<string, string[]> = {};
      rows.forEach((row) => {
        next[row.label] = [...row.selectedOptions];
      });
      setOptionSelections(next);
      setDraftTabKey(showFilterTabs ? (selectedTabKey ?? "all") : "all");
    } else {
      setActiveFilterLabel(null);
      setOptionSelections(getClearedSelections());
    }
  }, [open, rows, selectedTabKey, showFilterTabs]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setActiveFilterLabel(null);
      setOptionSelections(getClearedSelections());
    }
    onOpenChange(nextOpen);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side={isMobileOrTablet ? "bottom" : "right"}
        overlayClassName={"z-[1001]"}
        className={
          isMobileOrTablet
            ? "inset-0 z-[1001] h-dvh w-full rounded-none border-0 bg-surface-primary p-0"
            : "inset-y-0 right-0 left-auto z-[1001] h-full w-[360px] max-w-[90vw] rounded-none border-0 border-l bg-surface-primary p-0"
        }
      >
        <div className="flex h-full w-full flex-col">
          <header className="border-b border-slate-200 bg-white">
            <div className="flex items-center justify-between px-4 py-4">
              {activeFilter ? (
                <button
                  type="button"
                  className="flex items-center gap-1 text-base font-medium leading-4 tracking-tight text-black"
                  onClick={() => setActiveFilterLabel(null)}
                  aria-label="Назад"
                >
                  <ChevronLeft className="h-6 w-6" strokeWidth={1.5} />
                </button>
              ) : (
                <button
                  type="button"
                  className="text-base font-medium leading-4 tracking-tight text-brand-blue"
                  onClick={() => {
                    setOptionSelections(getClearedSelections());
                    setDraftTabKey("all");
                  }}
                >
                  {t(Namespace.Common, "reset")}
                </button>
              )}
              <h2 className="text-center text-[22px] font-bold leading-7 tracking-[-0.11px] text-slate-900">
                {activeFilter ? activeFilter.label : t(Namespace.Govdash, "filter.title")}
              </h2>
              <button
                type="button"
                aria-label={t(Namespace.Govdash, "actions.close")}
                onClick={() => handleOpenChange(false)}
                className="flex size-8 items-center justify-center rounded-full bg-slate-900 text-white"
              >
                <X className="size-4" strokeWidth={2.5} aria-hidden />
              </button>
            </div>
          </header>

          <div className="flex-1 overflow-y-auto">
            {!activeFilter ? (
              <>
                {showFilterTabs ? (
                  <div className="flex justify-center border-b border-slate-200 p-4">
                    <div className="inline-flex h-10 items-center rounded-xl bg-brand-blue-light p-1">
                      {tabs.map((tab) => {
                        const active = tab.key === draftTabKey;
                        return (
                          <button
                            key={tab.key}
                            type="button"
                            onClick={() => setDraftTabKey(tab.key)}
                            className={`flex h-8 items-center gap-2 rounded-lg px-4 ${
                              active
                                ? "bg-brand-blue text-surface-primary shadow-sm"
                                : "text-slate-900"
                            }`}
                          >
                            <span className="text-sm font-medium leading-[14px] tracking-tight">
                              {tab.label}
                            </span>
                            {typeof tab.count === "number" ? (
                              <span
                                className={`flex size-4 items-center justify-center rounded-md text-[9px] font-semibold leading-[9px] tracking-[-0.045px] ${
                                  active
                                    ? "bg-white text-brand-blue"
                                    : "bg-brand-blue text-text-invert"
                                }`}
                              >
                                {tab.count}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                {rows.map((row) => (
                  <button
                    key={row.key}
                    type="button"
                    data-testid={`filter-row-${row.key}`}
                    onClick={() => {
                      setActiveFilterLabel(row.key);
                      onExtraFilterClick?.(row);
                    }}
                    className="flex w-full items-start justify-between border-b border-slate-200 p-4 text-left"
                  >
                    <div className="flex min-w-0 flex-1 flex-col gap-2 pr-2">
                      <span className="text-sm font-normal leading-[14px] tracking-tight text-slate-900">
                        {row.label}
                      </span>
                      {(optionSelections[row.label] || []).length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {(optionSelections[row.label] || []).map((selectedValue) => {
                            const optionLabel =
                              row.options.find((option) => option.value === selectedValue)?.label ||
                              selectedValue;
                            return (
                              <span
                                key={`${row.label}-${selectedValue}`}
                                className="inline-flex h-6 min-w-0 max-w-full items-center gap-1 rounded-2xl bg-brand-blue px-1 text-sm font-medium leading-[14px] tracking-tight text-text-invert"
                              >
                                <span className="min-w-0 truncate px-2" title={optionLabel}>
                                  {optionLabel}
                                </span>
                                <div
                                  role="button"
                                  aria-label={`Удалить ${optionLabel}`}
                                  className="inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-text-invert text-brand-blue hover:bg-white/30"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setOptionSelections((prev) => {
                                      const current = prev[row.label] || [];
                                      return {
                                        ...prev,
                                        [row.label]: current.filter(
                                          (value) => value !== selectedValue,
                                        ),
                                      };
                                    });
                                  }}
                                >
                                  <X className="size-3" strokeWidth={2.5} aria-hidden />
                                </div>
                              </span>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                    <ChevronRight
                      className="mt-0.5 size-4 shrink-0 text-slate-700"
                      strokeWidth={2.25}
                      aria-hidden
                    />
                  </button>
                ))}
              </>
            ) : activeFilter.options.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">Нет доступных опций</div>
            ) : (
              activeFilter.options.map((option) => {
                const selectedValues = optionSelections[activeFilter.label] || [];
                const checked = selectedValues.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    data-testid={`filter-option-${option.value}`}
                    className="flex h-16 w-full items-center gap-6 border-b border-slate-200 px-4 text-left transition-colors bg-white"
                    onClick={() => {
                      setOptionSelections((prev) => {
                        const current = prev[activeFilter.label] || [];
                        const next = current.includes(option.value)
                          ? current.filter((v) => v !== option.value)
                          : activeFilter.singleSelect
                            ? [option.value]
                            : [...current, option.value];
                        return { ...prev, [activeFilter.label]: next };
                      });
                    }}
                  >
                    <span
                      className={`flex size-5 items-center justify-center rounded-full border ${
                        checked
                          ? "border-text-invert text-text-invert bg-brand-blue"
                          : "border-slate-300 bg-gray-300 text-transparent"
                      }`}
                    >
                      <Check className="size-3.5" strokeWidth={2.5} aria-hidden />
                    </span>
                    <span className="text-sm leading-[14px] tracking-tight font-normal text-slate-900">
                      {option.label}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <footer className="bg-text-invert p-4">
            <Button
              type="button"
              data-testid="filter-apply"
              className="h-12 w-full rounded-lg bg-brand-blue text-base font-medium leading-4 tracking-tight text-text-invert hover:opacity-95"
              onClick={() => {
                if (activeFilter) {
                  setActiveFilterLabel(null);
                  return;
                }
                onApply({
                  selectedTabKey: showFilterTabs ? draftTabKey : "all",
                  newSelected:
                    rows.map((filter) => ({
                      ...filter,
                      selectedOptions: optionSelections[filter.label] ?? [],
                    })) ?? [],
                });
              }}
            >
              {activeFilter ? t(Namespace.Common, "done") : t(Namespace.Common, "apply")}
            </Button>
          </footer>
        </div>
      </SheetContent>
    </Sheet>
  );
};
